import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Audit } from '@audit/shared-types';

export default function AuditPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: previousAudits = [], isLoading: previousAuditsLoading } = useQuery({
    queryKey: ['audits', 'page', pageId],
    queryFn: () =>
      apiClient
        .get<{ data: Audit[]; total: number }>(`/audits?pageId=${pageId ?? ''}&perPage=10`)
        .then((r) => r.data.data),
    enabled: !!pageId,
  });

  const handleRun = async () => {
    if (!pageId) return;
    setRunning(true);
    setError(null);
    try {
      const res = await apiClient.post<Audit>('/audits', {
        pageId: parseInt(pageId, 10),
      });
      void navigate(`/audits/${res.data.id}`);
    } catch {
      setError('Failed to start audit. Please try again.');
      setRunning(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="max-w-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Run Accessibility Audit</h2>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        <div>
          <p className="text-sm font-semibold text-gray-700">Audit Scope</p>
          <p className="text-sm text-gray-500 mt-1">
            Audits run against all WCAG levels (`A`, `AA`, `AAA`) and both supported versions (`2.1` and `2.2`) by default.
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          onClick={() => void handleRun()}
          disabled={running}
          className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors"
        >
          {running ? 'Starting audit…' : 'Run Audit'}
        </button>
      </div>

      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Previous Audits For This Page</h3>
          {!previousAuditsLoading && previousAudits.length > 0 && (
            <span className="text-xs text-gray-500">Showing latest {previousAudits.length}</span>
          )}
        </div>

        {previousAuditsLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading previous audits…</div>
        ) : previousAudits.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No previous audits for this page.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
                <th className="px-6 py-3">Audit ID</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Issues</th>
                <th className="px-6 py-3">Created</th>
                <th className="px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {previousAudits.map((audit) => (
                <tr key={audit.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-3 font-mono text-xs text-gray-700">#{audit.id}</td>
                  <td className="px-6 py-3">
                    <StatusBadge status={audit.status} />
                  </td>
                  <td className="px-6 py-3 text-gray-700">{audit.issueCount}</td>
                  <td className="px-6 py-3 text-gray-500">
                    {new Date(audit.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-3">
                    <Link
                      to={`/audits/${audit.id}`}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      Open audit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colours: Record<string, string> = {
    complete: 'bg-green-100 text-green-700',
    running: 'bg-yellow-100 text-yellow-700',
    pending: 'bg-gray-100 text-gray-600',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colours[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}
